import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useOrder, TransportMode } from '../context/OrderContext'
import { useAuth } from '../context/AuthContext'
import Stepper from "../components/Stepper"
import OrderSummary from "../components/OrderSummary"
import {
  CRISSIER,
  geocodeAddress,
  fetchSuggestions,
  haversineDistance,
} from '../lib/locationApi'


const PickupPage: React.FC = () => {
  const navigate = useNavigate()
  const { goToStep, canGoToStep, draft, setPickup } = useOrder()
  const { profile } = useAuth()

  const [pickupModes, setPickupModes] = useState<TransportMode[]>([])
  const [selectedMode, setSelectedMode] = useState<TransportMode | null>(null)

  const [pickupAddress, setPickupAddress] = useState('')
  const [suggestions, setSuggestions] = useState<any[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef<number | null>(null)

  const [distanceKm, setDistanceKm] = useState<number | null>(null)
  const [pickupPrice, setPickupPrice] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)

  // Load pickup modes
  useEffect(() => {
    if (!draft.service) {
      navigate('/order/start')
      return
    }

    const loadData = async () => {
      const { data } = await supabase
        .from('transport_modes')
        .select('*')
        .eq('type_mode', 'PICKUP')
        .eq('is_active', true)
        .order('label')

      setPickupModes(data ?? [])
      setLoading(false)
    }

    loadData()
  }, [draft.service, navigate])

  // Pre-fill address
  useEffect(() => {
    if (profile?.address_line1) {
      setPickupAddress(profile.address_line1)
    }
  }, [profile])

  useEffect(() => {
    goToStep('PICKUP')
  }, [goToStep])

  const modeNeedsAddress =
    selectedMode?.pricing_mode === "PER_KM"

  // ---------- AUTOCOMPLETE (debounced) ----------
  const handleAddressInput = (value: string) => {
    setPickupAddress(value)
    setPickupPrice(null)
    setDistanceKm(null)

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (value.length < 3) {
      setSuggestions([])
      return
    }

    debounceRef.current = window.setTimeout(async () => {
      const results = await fetchSuggestions(value)
      setSuggestions(results || [])
      setShowSuggestions(true)
    }, 300)
  }

  const selectSuggestion = (item: any) => {
    setPickupAddress(item.display_name)
    setSuggestions([])
    setShowSuggestions(false)
    setPickupPrice(null)
    setDistanceKm(null)
  }

  // ---------- PRICE CALC ----------
const handleCalculatePickupPrice = async () => {
  if (!selectedMode) {
    alert("Choisis un mode de prise en charge.")
    return
  }

  if (!pickupAddress) {
    alert("Merci d'indiquer ton adresse.")
    return
  }

  const geo = await geocodeAddress(pickupAddress)
  if (!geo) {
    alert("Adresse introuvable.")
    return
  }

  const km = haversineDistance(CRISSIER.lat, CRISSIER.lng, geo.lat, geo.lng)
  setDistanceKm(km)

  // 🔢 distance max depuis le mode (fallback 50 km)
  const maxDistanceKm = selectedMode.max_distance_km ?? 50

  if (km > maxDistanceKm) {
    setPickupPrice(null)
    alert(`Prise en charge hors zone (max ${maxDistanceKm} km autour de Crissier).`)
    return
  }

  if (selectedMode.pricing_mode === "PER_KM") {
    const pricePerKm = selectedMode.price_per_km ?? 0
    const minPrice = selectedMode.min_price ?? 0
    const price = Math.max(minPrice, km * pricePerKm)
    setPickupPrice(price)
  } else {
    setPickupPrice(selectedMode.fixed_price ?? 0)
  }
}

  // ---------- VALIDATE ----------
  const handleValidate = () => {
    if (!selectedMode) return alert("Choisis un mode.")
    if (modeNeedsAddress && (!pickupAddress || pickupPrice == null))
      return alert("Merci de valider l'adresse avant de continuer.")

    setPickup(
      selectedMode,
      modeNeedsAddress ? pickupAddress : undefined,
      distanceKm ?? 0,
      pickupPrice ?? (selectedMode.fixed_price ?? 0)
    )

    navigate("/order/delivery")
  }

  if (loading) return <p>Chargement…</p>

  return (
    <div className="card">
      <Stepper />

      <div className="card-header">
        <h1 className="card-title">Mode de prise en charge</h1>
      </div>

      {/* PICKUP MODES */}
      <div className="grid-2" style={{ marginTop: '1rem' }}>
        {pickupModes.map((mode) => (
          <button
            key={mode.id}
            className={`card ${selectedMode?.id === mode.id ? 'card--active' : ''}`}
            onClick={() => {
              setSelectedMode(mode)
              setPickupPrice(null)
              setDistanceKm(null)
            }}
          >
            <div className="card-header">
              <div>
                <div className="card-title">{mode.label}</div>
                <div>{mode.description}</div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* ADDRESS INPUT + AUTOCOMPLETE */}
      {modeNeedsAddress && (
        <div style={{ marginTop: '1.5rem', position: "relative" }}>
          <label className="label">Adresse</label>
          <input
            className="input"
            value={pickupAddress}
            onChange={(e) => handleAddressInput(e.target.value)}
            placeholder="Rue, NPA, Ville"
            autoComplete="off"
          />

          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div
              style={{
                position: "relative",
                left: 0,
                right: 0,
                background: 'var(--tun-dropdown-bg)',
                border: '1px solid var(--tun-dropdown-border)',
                borderRadius: "0.5rem",
                zIndex: 70,
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {suggestions.map((s, i) => (
                <div
                  key={i}
                  onClick={() => selectSuggestion(s)}
                  style={{
                    padding: "0.6rem",
                    cursor: "pointer",
                    borderBottom: '1px solid var(--tun-dropdown-border)',
                  }}
                >
                  {s.display_name}
                </div>
              ))}
            </div>
          )}

          <button data-testid="calculate-distance-button"
          className="btn btn-secondary" 
          style={{ marginTop: ".5rem" }} onClick={handleCalculatePickupPrice}>
            Valider cette adresse (et calculer le tarif)
          </button>

          {pickupPrice != null && (
            <p style={{ marginTop: ".5rem", color: "#22c55e" }}>
              Distance : {distanceKm?.toFixed(1)} km — Prix : {pickupPrice.toFixed(2)} CHF
            </p>
          )}
        </div>
      )}

      <OrderSummary />

      <button data-testid="continue-button" className="btn btn-primary" style={{ marginTop: "1.5rem" }} onClick={handleValidate}>
        Continuer
      </button>
    </div>
  )
}

export default PickupPage
